// src/Manage/Contact.js - Modern Themed Support & Help Center
import { toLocalYMD } from "../utils/localDate";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { sendEmail, addData, getData, deleteData } from "../service/APIService";
import uuid from "react-uuid";
import { Modal } from "react-bootstrap";
import config from "../config.json";
import "../styles/Contact.css";

const CATEGORIES = [
  { id: "general issue", label: "General Issue", icon: "fi fi-rr-comment-alt" },
  { id: "app-suggestion", label: "App Suggestion", icon: "fi fi-rr-bulb" },
  { id: "feedback", label: "Feedback", icon: "fi fi-rr-smile" },
  { id: "apps", label: "App Issue", icon: "fi fi-rr-bug" },
  { id: "others", label: "Others", icon: "fi fi-rr-apps" },
];

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general issue");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [supportCases, setSupportCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [dailySubmissionCount, setDailySubmissionCount] = useState(0);
  const [showCases, setShowCases] = useState(false);

  const location = useLocation();
  const maxcount = 5;

  const toggleSupportCases = () => {
    setShowCases((prev) => !prev);
  };

  const handleShowDetails = (caseItem) => {
    setSelectedCase(caseItem);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCase(null);
    setShowModal(false);
  };

  const fetchSupportCases = async (userEmail) => {
    try {
      const cases = await getData(userEmail, "contactus");
      setSupportCases(cases || []);
      const today = toLocalYMD();
      const todayCount = (cases || []).filter((caseItem) => {
        const caseDate = toLocalYMD(caseItem.createddate);
        return caseDate === today;
      }).length;
      setDailySubmissionCount(todayCount);
      return todayCount;
    } catch (error) {
      console.error("Failed to fetch support cases:", error);
      return 0;
    }
  };

  useEffect(() => {
    try {
      const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const guestUser = JSON.parse(localStorage.getItem("guestUser"));
      if (sessionUser && sessionUser.email && sessionUser.email !== "guest") {
        setName(sessionUser.name || sessionUser.email.split("@")[0]);
        setEmail(sessionUser.email);
        setIsLoggedIn(true);
        fetchSupportCases(sessionUser.email);
      } else if (guestUser) {
        setName(guestUser.name && guestUser.name !== "guest" && guestUser.name !== "Guest User" ? guestUser.name : "");
        setEmail(guestUser.email && guestUser.email !== "guest" ? guestUser.email : "");
        setIsLoggedIn(false);
      } else {
        setName("");
        setEmail("");
        setIsLoggedIn(false);
      }
    } catch {
      setIsLoggedIn(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!description.trim()) {
      setMessage({ text: "Please enter a description.", type: "error" });
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      setMessage({ text: "Please provide a valid email address.", type: "error" });
      return;
    }

    const currentDate = new Date().toISOString();
    const contactData = {
      id: uuid(),
      email: email.trim(),
      name: name.trim() || "User",
      subject: category,
      description: description.trim(),
      type: "contactus",
      status: "pending",
      createddate: currentDate,
    };

    setLoading(true);

    try {
      if (!isLoggedIn) {
        const todayCount = await fetchSupportCases(email.trim());
        if (todayCount >= maxcount) {
          setMessage({
            text: `Daily request limit reached (${maxcount} submissions max per day).`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }

      // 1. Save ticket to database
      await addData(contactData);

      // 2. Dispatch email notification in background
      try {
        const htmlContent = `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 500px; padding: 20px; border: 1px solid #EDE8EF; border-radius: 16px;">
            <h3 style="color: #42224A; margin-top: 0;">Support Request: ${category.toUpperCase()}</h3>
            <p><strong>Description:</strong></p>
            <p style="background: #F7F4F7; padding: 12px; border-radius: 8px;">${description.trim()}</p>
            <p><strong>Submitted by:</strong> ${name.trim() || "User"} (${email.trim()})</p>
            <p><strong>Ticket ID:</strong> ${contactData.id}</p>
            <p><strong>Date:</strong> ${new Date(currentDate).toLocaleString()}</p>
          </div>
        `;
        await sendEmail({
          sender: process.env.REACT_APP_SENDEREMAIL || config.supportemail,
          recipient: process.env.REACT_APP_SENDEREMAIL || config.supportemail,
          subject: `[${config.apptitle || "Pocket App"} Support] ${category.toUpperCase()} - ${name.trim() || "User"}`,
          cc: email.trim(),
          body: htmlContent,
        });
      } catch (emailErr) {
        console.warn("Support email notification dispatched with non-fatal notice:", emailErr);
      }

      setMessage({
        text: "Your support request has been submitted successfully.",
        type: "success",
      });
      setDescription("");
      setCategory("general issue");

      if (isLoggedIn) {
        fetchSupportCases(email);
      }
    } catch (error) {
      console.error("Failed to save contact details:", error);
      setMessage({
        text: "Failed to submit your request. Please check your connection and try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this support request?");
    if (!isConfirmed) return;

    try {
      await deleteData(id);
      setSupportCases((prevCases) => prevCases.filter((item) => item.id !== id));
      setMessage({ text: "Support request deleted successfully.", type: "success" });
    } catch (error) {
      console.error("Failed to delete case:", error);
      setMessage({ text: "Failed to delete the request. Please try again.", type: "error" });
    }
  };

  const backUrl = location.pathname.startsWith("/app1") ? "/app1/profile" : "/manage/profile";

  return (
    <div className="support-container">
      {/* Top Nav Header */}
      <div className="support-nav-header">
        <Link to={backUrl} className="support-back-btn">
          <i className="fi fi-rr-arrow-small-left"></i>
          <span>Back</span>
        </Link>
        <span className="support-nav-title">Support & Help</span>
        <div style={{ width: 68 }}></div>
      </div>

      {/* Hero Banner Card */}
      <div className="support-hero-card">
        <div className="support-hero-icon-wrap">
          <i className="fi fi-rr-headset"></i>
        </div>
        <h1 className="support-hero-title">Need Help or Have Feedback?</h1>
        <p className="support-hero-subtitle">
          Have an inquiry, suggestion, or issue? Our team is here to assist you with {config.apptitle || "Pocket App"}.
        </p>
        <span className="support-hero-pill">
          <i className="fi fi-rr-time-fast"></i> Average response: &lt; 24 hours
        </span>
      </div>

      {/* Quick Help Channels */}
      <div className="support-channels-grid">
        <div className="support-channel-card">
          <div className="support-channel-icon coral">
            <i className="fi fi-rr-envelope"></i>
          </div>
          <span className="support-channel-title">Direct Email</span>
          <p className="support-channel-desc">{config.supportemail}</p>
        </div>

        <Link to="/public/privacypolicy" className="support-channel-card">
          <div className="support-channel-icon mauve">
            <i className="fi fi-rr-shield-check"></i>
          </div>
          <span className="support-channel-title">Privacy Policy</span>
          <p className="support-channel-desc">Learn how your data is guarded</p>
        </Link>
      </div>

      {/* Guest Session Note */}
      {!isLoggedIn && (
        <div className="support-guest-card">
          <i className="fi fi-rr-info"></i>
          <p className="support-guest-text">
            You are submitting as a guest. To track and manage your submitted support tickets across sessions, please{" "}
            <Link to={backUrl}>Sign In</Link>.
          </p>
        </div>
      )}

      {/* Main Support Form Card */}
      <div className="support-form-card">
        <div className="support-card-header">
          <h2 className="support-card-title">
            <i className="fi fi-rr-paper-plane" style={{ color: "var(--accent-coral)" }}></i>
            Submit a Request
          </h2>
          <span className={`support-user-badge ${isLoggedIn ? "registered" : "guest"}`}>
            <i className={isLoggedIn ? "fi fi-rr-badge-check" : "fi fi-rr-user"}></i>
            {isLoggedIn ? "Registered User" : "Guest User"}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="support-form-row">
            {/* Name Field */}
            <div className="support-field-group">
              <label className="support-field-label">Your Name</label>
              <div className="support-input-wrap">
                <i className="fi fi-rr-user support-input-icon"></i>
                <input
                  type="text"
                  value={name}
                  maxLength={40}
                  placeholder="Enter your name"
                  onChange={(e) => setName(e.target.value)}
                  className="support-input-field"
                  disabled={isLoggedIn}
                  required
                />
                {isLoggedIn && <i className="fi fi-rr-lock support-locked-icon" title="Locked to account"></i>}
              </div>
            </div>

            {/* Email Field */}
            <div className="support-field-group">
              <label className="support-field-label">Your Email</label>
              <div className="support-input-wrap">
                <i className="fi fi-rr-envelope support-input-icon"></i>
                <input
                  type="email"
                  value={email}
                  placeholder="Enter your email"
                  onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                  className="support-input-field"
                  disabled={isLoggedIn}
                  required
                />
                {isLoggedIn && <i className="fi fi-rr-lock support-locked-icon" title="Locked to account"></i>}
              </div>
            </div>
          </div>

          {/* Category Selection */}
          <div className="support-field-group">
            <label className="support-field-label">Category</label>
            <div className="support-category-grid">
              {CATEGORIES.map((cat) => {
                const isActive = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`support-category-pill ${isActive ? "active" : ""}`}
                    onClick={() => setCategory(cat.id)}
                  >
                    <i className={cat.icon}></i>
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description Field */}
          <div className="support-field-group">
            <div className="support-field-label">
              <span>Description</span>
              <span className="support-char-counter">{500 - description.length} chars left</span>
            </div>
            <textarea
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
              className="support-textarea-field"
              placeholder="Describe your issue, feedback, or suggestion in detail..."
              required
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="support-submit-btn"
            disabled={loading || (!isLoggedIn && dailySubmissionCount >= maxcount)}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Submitting Request...</span>
              </>
            ) : !isLoggedIn && dailySubmissionCount >= maxcount ? (
              <span>Daily Limit Reached</span>
            ) : (
              <>
                <span>Send Support Request</span>
                <i className="fi fi-rr-paper-plane"></i>
              </>
            )}
          </button>
        </form>

        {/* Status Message Banner */}
        {message.text && (
          <div className={`support-alert ${message.type}`}>
            <i
              className={
                message.type === "success"
                  ? "fi fi-rr-check-circle"
                  : "fi fi-rr-exclamation"
              }
            ></i>
            <span>{message.text}</span>
          </div>
        )}
      </div>

      {/* Support Cases History (Logged-In Users) */}
      {isLoggedIn && (
        <div className="support-tickets-card">
          <div className="support-tickets-header" onClick={toggleSupportCases}>
            <div className="support-tickets-title-group">
              <i className="fi fi-rr-ticket" style={{ color: "var(--accent-mauve)" }}></i>
              <h3 className="support-tickets-title">My Support Requests</h3>
              <span className="support-tickets-count-badge">{supportCases.length}</span>
            </div>
            <button type="button" className="support-tickets-toggle-btn" aria-label="Toggle support tickets">
              <span>{showCases ? "Hide" : "View"}</span>
              <i className={`fi fi-rr-angle-small-${showCases ? "up" : "down"}`}></i>
            </button>
          </div>

          {showCases && (
            <div className="support-tickets-list">
              {supportCases.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-muted)", fontSize: "13.5px" }}>
                  <i className="fi fi-rr-inbox" style={{ fontSize: "28px", display: "block", marginBottom: "6px" }}></i>
                  No support tickets submitted yet.
                </div>
              ) : (
                supportCases.map((caseItem) => {
                  const status = (caseItem.status || "pending").toLowerCase();
                  return (
                    <div key={caseItem.id} className="support-ticket-item">
                      <div className="support-ticket-item-top">
                        <span className="support-ticket-category-tag">{caseItem.subject}</span>
                        <span className={`support-status-badge ${status}`}>
                          {status}
                        </span>
                      </div>

                      <p className="support-ticket-desc-preview">{caseItem.description}</p>

                      <div className="support-ticket-item-bottom">
                        <span className="support-ticket-date">
                          <i className="fi fi-rr-calendar"></i>
                          {new Date(caseItem.createddate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>

                        <div className="support-ticket-actions">
                          <button
                            type="button"
                            className="support-action-btn view"
                            onClick={() => handleShowDetails(caseItem)}
                            title="View Details"
                          >
                            <i className="fi fi-rr-eye"></i>
                            <span>Details</span>
                          </button>
                          <button
                            type="button"
                            className="support-action-btn delete"
                            onClick={() => deleteCase(caseItem.id)}
                            title="Delete Request"
                          >
                            <i className="fi fi-rr-trash"></i>
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Case Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered contentClassName="support-modal-themed">
        <Modal.Header closeButton className="support-modal-header">
          <Modal.Title>Support Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="support-modal-body">
          {selectedCase ? (
            <div>
              <div className="support-modal-field">
                <div className="support-modal-field-label">Ticket ID</div>
                <div className="support-modal-field-val" style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {selectedCase.id}
                </div>
              </div>

              <div className="support-modal-field">
                <div className="support-modal-field-label">Category</div>
                <div className="support-modal-field-val" style={{ textTransform: "capitalize" }}>
                  {selectedCase.subject}
                </div>
              </div>

              <div className="support-modal-field">
                <div className="support-modal-field-label">Status</div>
                <span className={`support-status-badge ${(selectedCase.status || "pending").toLowerCase()}`}>
                  {selectedCase.status || "pending"}
                </span>
              </div>

              <div className="support-modal-field">
                <div className="support-modal-field-label">Created Date</div>
                <div className="support-modal-field-val">
                  {new Date(selectedCase.createddate).toLocaleString()}
                </div>
              </div>

              <div className="support-modal-field">
                <div className="support-modal-field-label">Description</div>
                <div className="support-modal-desc-box">{selectedCase.description}</div>
              </div>
            </div>
          ) : (
            <p>Loading case details...</p>
          )}
        </Modal.Body>
        <Modal.Footer className="support-modal-footer">
          <button type="button" className="support-modal-close-btn" onClick={handleCloseModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Contact;
